import React from 'react';
import { Container, Row, Col, ProgressBar } from 'react-bootstrap';
import { motion } from 'framer-motion';
import './About.css';

const About = () => {
  // Professional skills with proficiency levels
  const skills = [
    { name: 'Microsoft Dynamics 365', level: 95 },
    { name: 'Power Platform (Power Apps, Power Automate)', level: 90 },
    { name: 'JavaScript / C#', level: 85 },
    { name: 'Azure Cloud Services', level: 80 },
    { name: 'Custom Plugin Development', level: 88 },
    { name: 'PCF Controls', level: 82 },
    { name: 'Power BI', level: 78 },
  ];

  // Education details
  const education = [
    {
      degree: 'Bachelor of Engineering (IT)',
      institution: 'Acropolis Institute of Technology & Research, Indore',
      year: '2017',
      grade: '7.69 CGPA',
      board: 'R.G.P.V. Bhopal'
    },
    {
      degree: 'Higher Secondary Certificate (HSC)',
      institution: 'Kendriya Vidhyalaya, Dewas',
      year: '2013',
      grade: '85.8%',
      board: 'CBSE'
    },
    {
      degree: 'Secondary School Certificate (SSC)',
      institution: 'Kendriya Vidhyalaya, Dewas',
      year: '2011',
      grade: '8.2 CGPA',
      board: 'CBSE'
    }
  ];

  // Work experience
  const experience = [
    {
      company: 'Delaware India',
      role: 'Senior Consultant',
      period: 'Oct 2023 - Present',
      description: 'Leading projects focused on Microsoft Dynamics 365 implementation and customization. Providing technical leadership and mentoring junior team members. Working closely with clients to understand requirements and deliver solutions that meet business needs.'
    },
    {
      company: 'Accenture',
      role: 'Application Development Specialist',
      period: 'Nov 2021 - Nov 2023',
      description: 'Specialized in Microsoft Dynamics 365 CE customization, configuration and implementation. Developed complex solutions aligned with business requirements, enhancing system efficiency. Guided support and development teams by allocating tasks based on skill sets and project needs.'
    },
    {
      company: 'NSquare Xperts Pvt. Ltd.',
      role: 'Sr. Dynamics CRM Developer',
      period: 'Jun 2020 - Nov 2021',
      description: 'Led technical design and development for multiple projects including health domain solutions. Created and managed Azure API Management, integration with third-party services, and customization of forms, views, and reports.'
    },
    {
      company: 'Soho Dragon',
      role: 'Dynamics CE Developer',
      period: 'Aug 2019 - Jun 2020',
      description: 'Developed Dynamics 365 apps including payment processing solutions. Implemented authentication using Azure and created Azure Functions for API triggers. Customized solutions for various client requirements.'
    },
    {
      company: 'Basecamp Startapps Pvt. Ltd.',
      role: 'Software Engineer',
      period: 'Jun 2017 - Aug 2019',
      description: 'Developed C# plugins for Microsoft Dynamics CRM solutions. Customized CRM organizations at the form, entity, relationship, reports, workflow, dialogs, and business unit levels. Created JavaScript for form customizations using JQuery, ODATA, and FetchXML.'
    }
  ];

  return (
    <section className="about">
      <Container>
        <motion.h1 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="section-title"
        >
          About Me
        </motion.h1>
        
        {/* Profile section */}
        <Row className="profile-section">
          <Col lg={4}>
            <motion.div 
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
              className="profile-image"
            >
              <img 
                src="https://via.placeholder.com/400x400.png?text=Ajyendra+Singh" 
                alt="Ajyendra Singh" 
                className="img-fluid" 
              />
            </motion.div>
          </Col>
          
          <Col lg={8}>
            <motion.div 
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <h2>Ajyendra Singh</h2>
              <h3>Microsoft Dynamics 365 & Power Platform Expert</h3>
              
              <div className="about-description">
                <p>
                  I'm a Senior Consultant with 8+ years of experience specializing in Microsoft Dynamics 365 customization, 
                  configuration, and implementation. My expertise extends to Power Platform tools such as Power Apps, 
                  Power Automate, and Power BI, enabling me to create comprehensive business solutions.
                </p>
                <p>
                  I've successfully delivered projects across various industries including Healthcare and Private Sector, 
                  focusing on creating efficient, user-friendly systems that address specific business challenges. 
                  My technical skills include custom code development using Plugins, JavaScript, APIs, PCF Controls, 
                  and Azure cloud services including Logic Apps, App Services, API Management, and Functions.
                </p>
                <p>
                  My goal is to leverage technology to deliver solutions that drive business value and improve 
                  operational efficiency. I'm passionate about staying current with the latest Microsoft technologies 
                  and continuously expanding my knowledge to provide the best solutions for my clients.
                </p>
              </div>
              
              <div className="personal-info">
                <Row>
                  <Col md={6}>
                    <ul>
                      <li><strong>Name:</strong> Ajyendra Singh</li>
                      <li><strong>Email:</strong> ajyendrasingh24@gmail.com</li>
                      <li><strong>Phone:</strong> +91 7691963979</li>
                    </ul>
                  </Col>
                  <Col md={6}>
                    <ul>
                      <li><strong>Location:</strong> Indore, Madhya Pradesh, India</li>
                      <li><strong>Experience:</strong> 8+ Years</li>
                      <li><strong>Availability:</strong> Available for Consulting</li>
                    </ul>
                  </Col>
                </Row>
              </div>
            </motion.div>
          </Col>
        </Row>
        
        {/* Skills section */}
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="skills-section section-spacing"
        >
          <h2 className="section-title">Professional Skills</h2>
          <Row>
            {skills.map((skill, index) => (
              <Col lg={6} key={index} className="mb-4">
                <div className="skill-item">
                  <div className="skill-info">
                    <h4>{skill.name}</h4>
                    <p>{skill.level}%</p>
                  </div>
                  <ProgressBar now={skill.level} variant="primary" />
                </div>
              </Col>
            ))}
          </Row>
        </motion.div>
        
        {/* Experience section */}
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="experience-section section-spacing"
        >
          <h2 className="section-title">Work Experience</h2>
          <div className="timeline">
            {experience.map((exp, index) => (
              <motion.div 
                initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="timeline-item" 
                key={index}
              >
                <div className="timeline-content">
                  <span className="timeline-date">{exp.period}</span>
                  <h3>{exp.role}</h3>
                  <h4>{exp.company}</h4>
                  <p>{exp.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
        
        {/* Education section */}
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="education-section section-spacing"
        >
          <h2 className="section-title">Education</h2>
          <Row>
            {education.map((edu, index) => (
              <Col lg={4} key={index} className="mb-4">
                <motion.div 
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="education-item"
                >
                  <h3>{edu.degree}</h3>
                  <p className="institution">{edu.institution}</p>
                  <div className="education-meta">
                    <span>{edu.year}</span>
                    <span className="separator">•</span>
                    <span>{edu.grade}</span>
                    <span className="separator">•</span>
                    <span>{edu.board}</span>
                  </div>
                </motion.div>
              </Col>
            ))}
          </Row>
        </motion.div>
        
        {/* Microsoft Certifications */}
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="certification-section section-spacing"
        >
          <h2 className="section-title">Microsoft Certifications</h2>
          <Row className="justify-content-center">
            <Col md={6} lg={3} className="mb-4">
              <div className="certification-item">
                <div className="cert-badge">
                  <img src="https://via.placeholder.com/150x150.png?text=PL-600" alt="PL-600" className="img-fluid" />
                </div>
                <h4>PL-600</h4>
                <p>Microsoft Power Platform Solution Architect</p>
              </div>
            </Col>
            <Col md={6} lg={3} className="mb-4">
              <div className="certification-item">
                <div className="cert-badge">
                  <img src="https://via.placeholder.com/150x150.png?text=PL-400" alt="PL-400" className="img-fluid" />
                </div>
                <h4>PL-400</h4>
                <p>Microsoft Power Platform Developer Associate</p>
              </div>
            </Col>
            <Col md={6} lg={3} className="mb-4">
              <div className="certification-item">
                <div className="cert-badge">
                  <img src="https://via.placeholder.com/150x150.png?text=PL-100" alt="PL-100" className="img-fluid" />
                </div>
                <h4>PL-100</h4>
                <p>Microsoft Power Platform App Maker</p>
              </div>
            </Col>
            <Col md={6} lg={3} className="mb-4">
              <div className="certification-item">
                <div className="cert-badge">
                  <img src="https://via.placeholder.com/150x150.png?text=PL-900" alt="PL-900" className="img-fluid" />
                </div>
                <h4>PL-900</h4>
                <p>Microsoft Power Platform Fundamentals</p>
              </div>
            </Col>
          </Row>
        </motion.div>
      </Container>
    </section>
  );
};

export default About;